/**
 * ForecastChart の外部凡例 (checkbox 群)。
 *
 * design 参照: design/demand/forecast-today.html L784-785 (`.dft-chart-legend`)。
 * design ではチャートの下ではなく weather-strip の「上」に独立配置する
 * (design L737-803 のカード内縦順序: 補正モード → 凡例 → 気象観測地点note →
 * weather-strip → チャート)。本コンポーネントは ForecastCard がその順序で
 * 配置する前提の純粋な表示コンポーネント (state は持たず ForecastCard が
 * hiddenKeys を所有する controlled component)。
 *
 * 系列一覧の算出は `models/chartLegend.ts` の `buildLegendItems` に委譲する
 * (ForecastChart の Line/Area 描画可否判定と同じ boolean を共有するため)。
 *
 * ## checkbox 本体の系列色 (2026-08-12 デザイナー判断)
 *
 * 凡例ドット (別置き swatch) は持たず、8080 準拠で **checkbox 本体に系列色を
 * 載せる**方式にする (design 実装: `css/components.css` の `.ecl-cb-series` +
 * `--ecl-cb-accent`、コメント「swatch を別置きしない代わりの表現」)。8080 は
 * native `<input type="checkbox">` を appearance:none で自前描画しているため
 * `input:checked { background: var(--ecl-cb-accent) }` で直接塗れるが、DS
 * `Checkbox` (chakra v3) は可視の箱を `[data-part="control"]` という別要素
 * (native input は sr-only) で描画するため、同じ CSS 変数方式は使えない
 * (`--ecl-cb-accent` を input に付けても兄弟要素の control には継承されない。
 * 2026-08-12 実装時に実 DOM で確認済み)。
 *
 * 代わりに DS `Checkbox` の `css` prop (chakra の style 上書き口) で
 * `[data-part="control"][data-state="checked"]` の background を直接指定する。
 * DS の recipe 本体は `@layer recipes { ... }` に入っており、`css` prop 経由の
 * 上書きは無 layer (= 常に layer 内より優先、CSS Cascade Layers の仕様) で
 * 出力されるため、checked の基本色だけでなく hover/active 派生セレクタも
 * まとめて上書きできる (2026-08-12 実装時に生成 CSS を確認して検証済み:
 * layer 無しの単一セレクタで hover/active 込み全上書きに足りた)。
 *
 * `items[].color` は ForecastCard が `buildLegendItems` の `colors` 引数へ渡す
 * 系列色で、ForecastChart の Line/Area stroke と必ず同一の出典を引くこと。
 * 汎用の `palette` prop のような「呼び出し側が任意色を渡せる口」ではない。
 */
import { useTranslation } from 'react-i18next'

import { Box, Checkbox, Flex, Text } from '@enechain/ecloud-designsystem'

import type {
  LegendGroup,
  LegendItem,
} from '~/pages/DemandForecastDaily/models/chartLegend'

type Props = {
  items: LegendItem[]
  hiddenKeys: Set<string>
  onToggle: (key: string) => void
}

/**
 * 枠を描く順序。 8080 `.dft-legend-2d` は 対象日 → 要素 → 実績トレンド予測 の順。
 * 見出しラベルの i18n key。 `trend` は 8080 も見出しを持たない (トグル 1 個だけの
 * 枠) ため `undefined`。
 */
const GROUP_ORDER: ReadonlyArray<{
  group: LegendGroup
  labelKey: string | undefined
}> = [
  {
    group: 'targetDate',
    labelKey: 'page:demandForecastDaily.chart.legendAxis.targetDate',
  },
  {
    group: 'element',
    labelKey: 'page:demandForecastDaily.chart.legendAxis.element',
  },
  { group: 'trend', labelKey: undefined },
]

export const ForecastChartLegend = ({
  items,
  hiddenKeys,
  onToggle,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  if (items.length === 0) return null
  return (
    // 8080 `.dft-legend-2d`: 枠を中央寄せで並べ、 折り返す。 高さは枠どうしで
    // 揃える (`align-items: stretch`) — 見出しラベルの右の区切り線を枠の上下いっぱいに
    // 伸ばすため。
    <Flex
      alignItems="stretch"
      justifyContent="center"
      flexWrap="wrap"
      gap={2}
      columnGap={3}
    >
      {GROUP_ORDER.map(({ group, labelKey }) => {
        const groupItems = items.filter((item) => item.group === group)
        if (groupItems.length === 0) return null
        return (
          // 8080 `.dft-legend-axis`: 枠線 + radius + 内側余白。
          <Flex
            key={group}
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            columnGap={2.5}
            borderWidth="1px"
            borderColor="Border.lowEmphasis"
            borderRadius="md"
            backgroundColor="Surface.primary"
            paddingX={2.5}
            paddingY={0.5}
          >
            {labelKey !== undefined && (
              // 8080 `.dft-legend-axis-label`: 「DS form-label 相当
              // (weight 600 / high-emphasis / md)」 + 右に区切り線。
              // インライン配置なので block/margin は付けない。
              <Text
                textStyle="label"
                alignItems="center"
                alignSelf="stretch"
                display="inline-flex"
                paddingRight={2.5}
                color="Text.highEmphasis"
                borderRightWidth="1px"
                borderRightColor="Border.lowEmphasis"
                whiteSpace="nowrap"
              >
                {t(labelKey)}
              </Text>
            )}
            {groupItems.map((item) => (
              <Checkbox
                key={item.key}
                checked={!hiddenKeys.has(item.key)}
                css={{
                  '& [data-part="control"][data-state="checked"]:not([data-disabled])':
                    { background: item.color },
                }}
                // 凡例ドット (別置き swatch) の代わりに checkbox 本体を系列色にする
                // (design 実装 `.ecl-cb-series` 相当、上部 JSDoc 参照)。checked 時の
                // control 背景だけを上書きし、hover/active/disabled は DS 既定の
                // フォールバック挙動 (disabled は quaternary へ戻る) に委ねる。
                onCheckedChange={() => {
                  onToggle(item.key)
                }}
              >
                <Flex alignItems="center" gap={1}>
                  {/* design 実測 (8080 `.ecl-checkbox`): font-size: var(--font-size-md) = 14px /
                color: var(--text-high-emphasis) */}
                  <Text color="Text.highEmphasis" fontSize="md">
                    {item.label}
                  </Text>
                  {/* 需要想定値の「編集対象」バッジ (design L2952) */}
                  {item.badge !== undefined && (
                    <Box
                      background="Surface.accentPrimaryLightLow"
                      borderRadius="sm"
                      paddingX={1}
                    >
                      {/* eslint-disable-next-line ebalance/no-adhoc-typography -- design は DS .badge md/400、実装は意図的小型 pill (xs/600)。バッジ統合トラックで是正予定 */}
                      <Text
                        color="Text.accentPrimary"
                        fontSize="xs"
                        fontWeight="semibold"
                      >
                        {item.badge}
                      </Text>
                    </Box>
                  )}
                </Flex>
              </Checkbox>
            ))}
          </Flex>
        )
      })}
    </Flex>
  )
}
